// v4.1 B3 (2026-11-03): the publishing kit of the latest version: the title, the description (chapters, sources and
// the AI disclosure in it), tags and hashtags, and each platform's post, each with a Copy button. Free; it's made
// from the version itself, and the user edits before pasting. The Director opens it with `fold` "publish".
import { useState } from "react";
import { api } from "../api";
import { toast, useRefresh } from "../store";
import type { Project, Version } from "../types";
import { Fold } from "./Fold";
import { Icon, useLoad } from "./bits";

type Post = { platform: string; title?: string; text: string; note?: string };
export type Kit = { title: string; description: string; chapters: { atS: number; label: string }[]; tags: string[]; hashtags: string[]; sourcesBlock: string | null; aiDisclosure: boolean; shortForm: boolean; posts: Post[] };

const PLATFORM: Record<string, string> = { youtube: "YouTube", shorts: "YouTube Shorts", tiktok: "TikTok", instagram: "Instagram Reels", x: "X", linkedin: "LinkedIn" };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");
export function normKit(v: unknown): Kit | null {
  const o = isObj(v) && isObj(v.kit) ? v.kit : null;
  if (!o || !str(o.title)) return null;
  return {
    title: str(o.title),
    description: str(o.description),
    chapters: (Array.isArray(o.chapters) ? o.chapters : []).filter(isObj).map((c) => ({ atS: Number(c.atS) || 0, label: str(c.label) })),
    tags: (Array.isArray(o.tags) ? o.tags : []).map(str).filter(Boolean),
    hashtags: (Array.isArray(o.hashtags) ? o.hashtags : []).map(str).filter(Boolean),
    sourcesBlock: str(o.sourcesBlock) || null,
    aiDisclosure: o.aiDisclosure === true,
    shortForm: o.shortForm === true,
    posts: (Array.isArray(o.posts) ? o.posts : []).filter(isObj).map((x) => ({ platform: str(x.platform), ...(str(x.title) ? { title: str(x.title) } : {}), text: str(x.text), ...(str(x.note) ? { note: str(x.note) } : {}) })),
  };
}

export function PublishKitFold({ p, latest }: { p: Project; latest: Version | null }) {
  const tick = useRefresh("project");
  const { data: kit } = useLoad(async () => (latest ? normKit(await api.publishKit(p.id, latest.version).catch(() => null)) : null), [p.id, latest?.version, tick]);
  const [open, setOpen] = useState<string | null>(null);
  if (!latest) return null;
  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`Copied the ${what}.`, "good");
    } catch {
      toast("Couldn't copy. Press and hold the text to copy it.", "bad");
    }
  };
  const row = (label: string, text: string, what: string, multi = false) => (
    <div className="pk-row">
      <div className="pk-head">
        <span className="field-label">{label}</span>
        <button type="button" className="btn btn-quiet btn-small" onClick={() => void copy(text, what)} aria-label={`Copy the ${what}`}>
          <Icon name="copy" size={14} /> Copy
        </button>
      </div>
      {multi ? <pre className="pk-text">{text}</pre> : <p className="pk-line">{text}</p>}
    </div>
  );
  return (
    <Fold id="video.publish" title="Publish" summary={kit ? `v${latest.version} · ${kit.shortForm ? "Shorts, TikTok, Reels" : "YouTube"}, X, LinkedIn` : "Getting it ready"}>
      {!kit ? (
        <p className="muted">The kit for version {latest.version} isn&apos;t ready yet.</p>
      ) : (
        <div className="pk">
          {row("Title", kit.title, "title")}
          {row("Description", kit.description, "description", true)}
          {kit.tags.length > 0 && row("Tags", kit.tags.join(", "), "tags")}
          <p className="field-hint">Mark it as altered or synthetic content where the platform asks: the pictures, video and voices were generated.</p>
          <div className="pk-posts">
            {kit.posts.map((x) => (
              <div key={x.platform} className="pk-post">
                <button type="button" className="pk-post-head" aria-expanded={open === x.platform} onClick={() => setOpen(open === x.platform ? null : x.platform)}>
                  <span>{PLATFORM[x.platform] ?? x.platform}</span>
                  <Icon name="chevron" size={14} />
                </button>
                {open === x.platform && (
                  <div className="pk-post-body">
                    {x.title && row("Title", x.title, `${PLATFORM[x.platform] ?? x.platform} title`)}
                    {row("Post", x.text, `${PLATFORM[x.platform] ?? x.platform} post`, true)}
                    {x.note && <p className="field-hint">{x.note}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Fold>
  );
}
